// VAT numbers VIES could not answer for (sculpt-api PLAN-payments.md P9): their orders are held until staff settle
// the number here, or VIES answers on a later pass. Below, the EU distance sales against the €10,000 threshold.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime, eur } from '../../services/format';
import { Alert, Card, Cell, PageTitle, Row, Table } from '../../components/ui';
import { VatDecisionButtons, decisionNotice } from '../../components/vat';
import type { EuDistanceSales } from '../../types/api';

export function VatReviews() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const { data, error, loading, reload } = useAsync(() => api.vatReviews(), []);
  const sales = useAsync(() => api.euDistanceSales(), []);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);

  return (
    <div>
      <PageTitle title={t('vat.title')} subtitle={t('vat.subtitle')} />
      {notice && <div className="mb-3"><Alert tone="good">{notice}</Alert></div>}
      {(fail || error) && <div className="mb-3"><Alert>{fail || error}</Alert></div>}
      <Table head={[t('orders.organization'), t('profile.country'), t('orgs.vat'), t('vat.onRecord'), t('vat.since'), t('vat.held'), '']} empty={loading ? t('common.loading') : t('vat.none')}>
        {(data ?? []).map((v) => (
          <Row key={v.organization_id}>
            <Cell>
              <Link to={`/organizations/${v.organization_id}`} className="text-accent underline">
                {v.organization_name}
              </Link>
              {v.billing_name && <div className="text-[11.5px] text-faint">{v.billing_name}</div>}
            </Cell>
            <Cell mono>{v.billing_country ?? '—'}</Cell>
            <Cell mono>{v.vat_number ?? '—'}</Cell>
            <Cell className="max-w-[260px]">
              {v.vat_check_name ? (
                <>
                  {v.vat_check_name}
                  {v.vat_check_address && <div className="text-[11.5px] text-faint">{v.vat_check_address}</div>}
                </>
              ) : (
                <span className="text-faint">{t('vat.noAnswer')}</span>
              )}
            </Cell>
            <Cell mono>{dateTime(v.updated_at, l)}</Cell>
            <Cell mono>{t('vat.heldOrders', { count: v.held_orders, amount: eur(v.held_amount, l) })}</Cell>
            <Cell>
              {v.vat_number && (
                <VatDecisionButtons
                  organizationId={v.organization_id}
                  number={v.vat_number}
                  onDone={(decision) => {
                    setFail(null);
                    setNotice(`${v.organization_name}: ${decisionNotice(decision, t)}`);
                    reload();
                  }}
                  onError={(err) => setFail(errorMessage(err))}
                />
              )}
            </Cell>
          </Row>
        ))}
      </Table>
      <div className="mt-6">{sales.data ? <DistanceSales sales={sales.data} /> : sales.error && <Alert>{sales.error}</Alert>}</div>
    </div>
  );
}

/** This year's and last year's EU distance sales against the warning and the threshold. */
export function DistanceSales({ sales }: { sales: EuDistanceSales }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const threshold = Number(sales.threshold);
  const warning = Number(sales.warning);
  const years: [number, number][] = [
    [sales.year, Number(sales.total)],
    [sales.year - 1, Number(sales.previous_year_total)],
  ];
  return (
    <Card title={t('vat.salesTitle')}>
      <p className="mb-3 text-[12.5px] text-muted">{t('vat.salesHint', { threshold: eur(threshold, l) })}</p>
      <div className="flex flex-col gap-3">
        {years.map(([year, total]) => {
          const ratio = threshold > 0 ? Math.min(1, total / threshold) : 0;
          const tone = total >= threshold ? 'bg-danger' : total >= warning ? 'bg-warn' : 'bg-accent';
          return (
            <div key={year} data-testid={`sales-${year}`}>
              <div className="flex items-baseline justify-between text-[12.5px]">
                <span className="font-medium text-ink">{year}</span>
                <span className="font-mono text-[11.5px] text-muted">
                  {eur(total, l)} / {eur(threshold, l)}
                  {total >= threshold ? ` · ${t('vat.salesOver')}` : total >= warning ? ` · ${t('vat.salesWarning', { warning: eur(warning, l) })}` : ''}
                </span>
              </div>
              <div className="relative mt-1 h-2 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
                <div className={`h-full rounded-full ${tone}`} style={{ width: `${ratio * 100}%` }} />
                <div className="absolute top-0 h-full w-px bg-warn" style={{ left: `${threshold > 0 ? (warning / threshold) * 100 : 0}%` }} title={eur(warning, l)} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
